import NamedIcon from '@v2/shared/components/NamedIcon';
import {
  isMagzterGold,
  isRestricted,
  PRODUCT_TYPE_POSTCARD,
  Restrictions,
  SaleCategory,
  SaleProduct,
  shouldHideProduct,
} from '@v2/shared/lib/megaSale';

import MagzterGoldPromo from './MagzterGoldPromo';
import PostcardsPromo from './PostcardsPromo';
import ProductSlot from './ProductSlot';

/**
 * The grid the tiles sit in.
 *
 * Two columns from `md` rather than `lg`, and three from `xl` rather than `2xl`.
 * Dropping the left rail gave the grid that column back at every width, which is the
 * reason the rail became a navbar.
 *
 * Three is where it stops. A fourth at `2xl` fits — 374px tiles leaving 154px beside the
 * 180px cover — but fitting and reading are different things, and at 154px the controls
 * and the button crowd the cover. Reverted after review.
 *
 * Exported for the same reason the tile exports its cover box: the loading screen lays
 * its placeholder cards out in this grid, and a skeleton in a different number of columns
 * is a page that rearranges itself the moment the sale arrives.
 */
export const PRODUCT_GRID = 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3';

/**
 * The sticky heading over one section's grid.
 *
 * Both offsets are measured rather than stated. The header is three heights across the
 * breakpoints, and the category navbar is one height on a desktop, two when its pills
 * wrap, and zero on a phone where it does not render — so any single number here is wrong
 * at most widths. It was `82px` and the mobile header is 53px, which left the heading
 * floating 29px below the header with products scrolling through the gap.
 */
export const CATEGORY_HEADING =
  'sticky z-[20] top-[calc(var(--sale-header-height,82px)+var(--sale-nav-height,0px))] bg-white flex items-center gap-2 m-0 py-2 px-3 text-lg font-semibold text-slate-900 border-b border-slate-100';

type Props = {
  category: SaleCategory;
  products: SaleProduct[];
  restrictions: Restrictions;
  highestTerm: boolean;
  showCoverPrice: boolean;
  /**
   * The product being scrolled to, if it is in this category: drawn for real whatever the
   * scroll position, so there is something to scroll *to*.
   */
  pinnedProductId: number | null;
  /**
   * The product whose detail view should open, and the request that asked for it.
   *
   * Separate from the pin, and later than it: the pin has to exist before the journey
   * starts, and the dialog must not open until it has finished. The request number is what
   * makes choosing the same product from the search twice open the dialog twice.
   */
  detail: { productId: number; request: number } | null;
  /** The dialog has been closed, so the request above has been answered. */
  onDetailClose: () => void;
  onAdd: (product: SaleProduct, skuCode: string, giftSkuCode: string | null, offerCode: string | null) => Promise<void>;
};

/**
 * One section of the sale: a heading and its products.
 *
 * The restriction filter runs here rather than inside the tile so that a hidden product
 * occupies no grid position — filtering inside the tile would leave a hole where it was.
 * It has to run on every category the product appears in, which is why the same title
 * can be hidden more than once.
 */
export default function CategorySection({
  category,
  products,
  restrictions,
  highestTerm,
  showCoverPrice,
  pinnedProductId,
  detail,
  onDetailClose,
  onAdd,
}: Props) {
  const visible = products.filter((product) => !shouldHideProduct(product, restrictions));

  /*
   * Magzter Gold sits above the grid rather than in it — legacy's `categories.js` finds
   * it by slug and draws it before the row of tiles, and `shouldHideProduct` has always
   * kept it out of the grid for exactly that reason. What was missing was the other half:
   * nothing drew it, so on a sale whose "Unlimited Digital Magazines" section holds only
   * this product, the section rendered nothing at all while the navbar went on offering a
   * pill that scrolled nowhere. Live on labor26 until now.
   */
  const magzterGold = products.find(
    (product) => isMagzterGold(product) && !isRestricted(product, restrictions)
  );

  if (visible.length === 0 && !magzterGold) {
    return null;
  }

  /*
   * A section of postcards opens on the explainer rather than on its heading. The test is
   * legacy's — the section's first product — and in production a section holding one
   * postcard holds nothing else.
   *
   * The explainer does not take the heading's `sect<id>`. To the scroll and to the navbar
   * that id is the sticky heading, whose resting place they read off it, and the explainer
   * does not stick. With no heading they measure against the header and the navbar, which
   * is exactly where the top of this section comes to rest.
   */
  const isPostcardSection = products[0].product_type === PRODUCT_TYPE_POSTCARD;
  const headingId = isPostcardSection ? `postcards${category.id}` : `sect${category.id}`;

  return (
    <section id={`sect_wrapper${category.id}`} aria-labelledby={headingId}>
      {isPostcardSection ? (
        <PostcardsPromo headingId={headingId} />
      ) : (
        <h2 id={headingId} className={CATEGORY_HEADING}>
          {category.icon && <NamedIcon name={category.icon} prefix="fas" className="text-primary" fixedWidth />}
          {category.name}
        </h2>
      )}

      {magzterGold && <MagzterGoldPromo product={magzterGold} onAdd={onAdd} />}

      <div className={PRODUCT_GRID}>
        {visible.map((product) => (
          <ProductSlot
            key={product.id}
            product={product}
            categoryId={category.id}
            highestTerm={highestTerm}
            showCoverPrice={showCoverPrice}
            openDetail={detail?.productId === product.id}
            detailRequest={detail?.request}
            onDetailClose={onDetailClose}
            pinned={pinnedProductId === product.id}
            onAdd={onAdd}
          />
        ))}
      </div>
    </section>
  );
}
